/**
 * Timezone-free calendar dates.
 *
 * SPEC §3.5, §5: availability dates are *calendar* dates, not instants. The classic
 * failure is `new Date("2026-03-05")`, which JS parses as UTC midnight and then renders
 * as March 4 anywhere west of Greenwich. That is the most likely cause of the prototype's
 * September/October rendering bug, and with instructors across four timezones it would
 * recur constantly.
 *
 * So: no `Date` object is used for any calendar arithmetic in this module. Everything is
 * integer math on (year, month, day). `Date` appears in exactly one place — `todayIn()` —
 * because "what is today in Henderson right now" genuinely requires a clock.
 */
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function parseISO(iso) {
    const m = ISO_RE.exec(iso);
    if (!m)
        throw new RangeError(`Not an ISO calendar date: ${JSON.stringify(iso)}`);
    const date = { year: +m[1], month: +m[2], day: +m[3] };
    if (!isValid(date))
        throw new RangeError(`Not a real calendar date: ${iso}`);
    return date;
}
export function toISO(d) {
    return `${String(d.year).padStart(4, '0')}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export function daysInMonth(year, month) {
    if (month < 1 || month > 12)
        throw new RangeError(`Month out of range: ${month}`);
    return month === 2 && isLeapYear(year) ? 29 : MONTH_LENGTHS[month - 1];
}
export function isValid(d) {
    return (Number.isInteger(d.year) &&
        Number.isInteger(d.month) &&
        Number.isInteger(d.day) &&
        d.month >= 1 &&
        d.month <= 12 &&
        d.day >= 1 &&
        d.day <= daysInMonth(d.year, d.month));
}
/**
 * Days since 1970-01-01, by Howard Hinnant's `days_from_civil`. Pure integer math,
 * correct for any proleptic Gregorian date, and the basis for every other operation here.
 */
export function toEpochDay(d) {
    const y = d.year - (d.month <= 2 ? 1 : 0);
    const era = Math.floor((y >= 0 ? y : y - 399) / 400);
    const yoe = y - era * 400; // [0, 399]
    const doy = Math.floor((153 * (d.month + (d.month > 2 ? -3 : 9)) + 2) / 5) + d.day - 1;
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
    return era * 146097 + doe - 719468;
}
/** Inverse of `toEpochDay` (`civil_from_days`). */
export function fromEpochDay(epochDay) {
    const z = epochDay + 719468;
    const era = Math.floor((z >= 0 ? z : z - 146096) / 146097);
    const doe = z - era * 146097; // [0, 146096]
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
    const y = yoe + era * 400;
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const mp = Math.floor((5 * doy + 2) / 153);
    const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
    const month = mp + (mp < 10 ? 3 : -9);
    return { year: y + (month <= 2 ? 1 : 0), month, day };
}
/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeekIndex(d) {
    const z = toEpochDay(d);
    return z >= -4 ? (z + 4) % 7 : ((z + 5) % 7) + 6;
}
export const WEEKDAYS = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
];
export function dayOfWeek(d) {
    return WEEKDAYS[dayOfWeekIndex(d)];
}
export function addDays(d, days) {
    return fromEpochDay(toEpochDay(d) + days);
}
/** `a - b`, in whole days. */
export function differenceInDays(a, b) {
    return toEpochDay(a) - toEpochDay(b);
}
/** Negative if a < b, 0 if equal, positive if a > b. */
export function compare(a, b) {
    return toEpochDay(a) - toEpochDay(b);
}
export function isBefore(a, b) {
    return compare(a, b) < 0;
}
export function isAfter(a, b) {
    return compare(a, b) > 0;
}
export function isSameDay(a, b) {
    return compare(a, b) === 0;
}
/** Inclusive on both ends. */
export function isWithin(d, from, to) {
    if (compare(d, from) < 0)
        return false;
    return to === undefined || compare(d, to) <= 0;
}
const CYCLE_RE = /^(\d{4})-(\d{2})$/;
export function parseCycleKey(key) {
    const m = CYCLE_RE.exec(key);
    if (!m)
        throw new RangeError(`Not a cycle key: ${JSON.stringify(key)}`);
    const year = +m[1];
    const month = +m[2];
    if (month < 1 || month > 12)
        throw new RangeError(`Month out of range in cycle key: ${key}`);
    return { year, month };
}
export function toCycleKey(year, month) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}
export function cycleKeyOf(d) {
    return toCycleKey(d.year, d.month);
}
/** Advance a cycle key by whole months. Handles year rollover in both directions. */
export function addMonths(key, months) {
    const { year, month } = parseCycleKey(key);
    const total = year * 12 + (month - 1) + months;
    return toCycleKey(Math.floor(total / 12), (((total % 12) + 12) % 12) + 1);
}
/** Every date in the cycle's month, in order. */
export function datesInCycle(key) {
    const { year, month } = parseCycleKey(key);
    const out = [];
    for (let day = 1; day <= daysInMonth(year, month); day++)
        out.push({ year, month, day });
    return out;
}
/**
 * Today's calendar date in an IANA timezone.
 *
 * The only place in this module that touches the clock. `en-CA` is used because it
 * formats as `YYYY-MM-DD`; `formatToParts` would also work but is more code for the
 * same result.
 */
export function todayIn(timeZone, now = new Date()) {
    const iso = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
    return parseISO(iso);
}
//# sourceMappingURL=calendar.js.map